"use client";

import { useServerInsertedHTML } from "next/navigation";

interface FontSource {
  ttf?: string; // URL to .ttf file
  woff?: string; // URL to .woff file
  woff2?: string; // URL to .woff2 file
  weight: number; // Font weight (e.g., 400, 700)
  style: string; // Font style (e.g., 'normal', 'italic')
}
interface FontConfig {
  family: string; // Font family name
  sources: FontSource[];
  fallback: string; // Fallback font stack
}

interface FontProviderProps {
  typography: any;
}

export function FontProvider({ typography }: FontProviderProps) {
  useServerInsertedHTML(() => {
    const generateFontFaces = (fontConfig: FontConfig) => {
      return fontConfig.sources
        .map(
          (source) => `
        @font-face {
          font-family: '${fontConfig.family}';
          src: ${[
            source.woff2 && `url('${source.woff2}') format('woff2')`,
            source.woff && `url('${source.woff}') format('woff')`,
            source.ttf && `url('${source.ttf}') format('truetype')`,
          ]
            .filter(Boolean)
            .join(",")};
          font-weight: ${source.weight};
          font-style: ${source.style};
          font-display: swap;
        }
      `,
        )
        .join("\n");
    };

    const styles = `
      ${generateFontFaces(typography.headingFont)}
      ${generateFontFaces(typography.bodyFont)}
      
      :root {
        --config-heading-font: ${typography.headingFont.family}, ${typography.headingFont.fallback};
        --config-body-font: ${typography.bodyFont.family}, ${typography.bodyFont.fallback};
      }

      .config-h1, .config-h2, .config-h3, .config-h4, .config-h5 {
        font-family: var(--config-heading-font);
      }

      .config-p1, .config-p2 , .config-p3{
        font-family: var(--config-body-font);
      }

      @media (min-width: 320px) { 
        .config-h1 {
          font-size: 42px;    /* Reduced from 60px */
          line-height: 46px;  /* Slightly larger than font-size for readability */
        }

        .config-h2 {
          font-size: 36px;    /* Reduced from 52px */
          line-height: 40px;
        }

        .config-h3 {
          font-size: 32px;    /* Reduced from 40px */
          line-height: 36px;
        }

        .config-h5 {
          font-size: 20px;    /* Reduced from 24px */
          line-height: 24px;
        }

        .config-p1 {
          font-size: 24px;    /* Reduced from 32px */
          line-height: 32px;
        }

        .config-p2 {
          line-height: 24px;
          font-size: 18px;
        }


        .config-p3 {
          line-height: 18px;
          font-size: 14px;
        }
      }

      @media (min-width: 768px) { 
        .config-h1 {
          font-size: 60px;
          line-height: 60px;
        }

        .config-h2 {
          font-size: 52px;
          line-height: 52px;
        }

        .config-h3 {
          line-height: 48px;
          font-size: 40px;
        }

        .config-h5 {
          line-height: 24px;
          font-size: 24px;
        }

        .config-p1 {
          line-height: 32px;
          font-size: 32px;
        }

        .config-p2 {
          line-height: 24px;
          font-size: 24px;
        }

        .config-p3 {
          line-height: 24px;
          font-size: 18px;
        }


      }
    `;

    // biome-ignore lint/security/noDangerouslySetInnerHtml:manual font injection
    return <style dangerouslySetInnerHTML={{ __html: styles }} />;
  });

  return null;
}
